import {
  LocalRecordingDBInstance,
  readFileFromOPFS,
  deleteOPFSFile,
  updateChunkStatus,
  deleteChunk,
  RecordingRow,
} from './LocalRecordingStorage';
import {BASE_URL} from '../context/FetchContext';

type OnCompleteFn = (trackId: string, totalChunksCount: number) => Promise<void>;

type QueueItem = { id: string; trackId: string };

const STALE_RECORDING_IDLE_MS = 90_000; // if no new chunks for this long, assume capture crashed/stopped
const MAX_SIGNED_URL_FETCH_404_RETRIES = 3; // stop retrying forever if backend never created the track entry

class BackgroundUploader {
  private running = false;
  private concurrency = 2;
  private active = 0;
  private queue: QueueItem[] = [];
  private onComplete?: OnCompleteFn;
  private signedUrlBatchCache: Map<string, string[]> = new Map();

  private async abandonRecording(trackId: string, reason: string): Promise<void> {
    try {
      console.error(`[Uploader] Abandoning local recording ${trackId}: ${reason}`);
      const chunks = await LocalRecordingDBInstance.chunks.where('trackId').equals(trackId).toArray();
      for (const c of chunks) {
        try {
          if (c.opfsPath) await deleteOPFSFile(c.opfsPath);
        } catch {}
        await deleteChunk(c.id).catch(() => {});
      }
      await LocalRecordingDBInstance.recordings.delete(trackId).catch(() => {});
    } catch (e) {
      console.warn('[Uploader] abandonRecording failed for', trackId, e);
    }
  }

  public configure(opts: { onComplete: OnCompleteFn }) {
    this.onComplete = opts.onComplete;
  }

  public start() {
    if (this.running) return;
    this.running = true;
    window.addEventListener('online', this.kick);
    this.kick();
  }

  public stop() {
    this.running = false;
    window.removeEventListener('online', this.kick);
  }

  public enqueue(item: QueueItem) {
    // avoid duplicates: simple check by id
    if (!this.queue.find((q) => q.id === item.id)) {
      this.queue.push(item);
    }
    this.kick();
  }

  private kick = () => {
    if (!this.running) return;
    void this.pump();
  };

  private async pump() {
    while (this.running && this.active < this.concurrency && this.queue.length > 0) {
      const item = this.queue.shift()!;
      this.active++;
      void this.uploadOne(item)
        .catch(() => {})
        .finally(() => {
          this.active--;
          this.kick();
        });
    }
  }

  private async uploadOne(item: QueueItem): Promise<void> {
    // Get the latest chunk row
    const row = await LocalRecordingDBInstance.chunks.get(item.id);
    if (!row) {
      // already uploaded/deleted
      await this.maybeComplete(item.trackId);
      return;
    }

    if (row.status === 'uploaded') {
      await this.maybeComplete(item.trackId);
      return;
    }

    // If offline, re-enqueue later
    if (!navigator.onLine) {
      // Simple reschedule
      setTimeout(() => this.enqueue(item), 2000);
      return;
    }

    // Ensure we have a signed URL; we allow chunks to be persisted before trackId arrives.
    let signedUrl = row.signedUrl;
    if (!signedUrl || signedUrl.trim() === '') {
      try {
        signedUrl = await this.getSignedUrlForChunk(row.trackId, row.seq);
      } catch (e: any) {
        const nextAttemptCount = (row.attemptCount || 0) + 1;
        const status = typeof e?.status === 'number' ? e.status : null;

        // If the backend never created the track entry, signed-url fetch will 404 forever.
        // After a while, stop hammering the server and delete the local chunks.
        if (status === 404 && nextAttemptCount >= MAX_SIGNED_URL_FETCH_404_RETRIES) {
          try {
            const rec = await LocalRecordingDBInstance.recordings.get(row.trackId);
            if (rec?.isRecordingDone) {
              await this.abandonRecording(row.trackId, `signed url fetch returned 404 ${nextAttemptCount} times`);
              return;
            }
          } catch {}
        }

        const delayMs = this.backoffMs(row.attemptCount || 0);
        await updateChunkStatus(row.id, 'failed', {
          lastError: e?.message ?? 'signed url fetch failed',
          lastAttemptAt: Date.now(),
          attemptCount: nextAttemptCount,
        });
        setTimeout(() => this.enqueue(item), delayMs);
        return;
      }

      // Still no signedUrl (most commonly: serverTrackId not known yet). Retry shortly.
      if (!signedUrl) {
        setTimeout(() => this.enqueue(item), 1000);
        return;
      }

      // Persist the signed URL so resumePendingOnInit can upload without re-fetching.
      await updateChunkStatus(row.id, row.status === 'failed' ? 'pending' : row.status, { signedUrl });
    }

    // Prepare blob (do this after we have a signed URL to avoid heavy reads while trackId is still pending).
    let blob: Blob | undefined;
    try {
      if (row.opfsPath) {
        const file = await readFileFromOPFS(row.opfsPath);
        blob = file;
      }
    } catch (e) {
      // missing file; mark failed
      await updateChunkStatus(row.id, 'failed', { lastError: 'missing local file', lastAttemptAt: Date.now(), attemptCount: (row.attemptCount || 0) + 1 });
      return;
    }
    if (!blob) {
      await updateChunkStatus(row.id, 'failed', { lastError: 'no blob', lastAttemptAt: Date.now(), attemptCount: (row.attemptCount || 0) + 1 });
      return;
    }

    // Mark uploading
    await updateChunkStatus(row.id, 'uploading', { lastAttemptAt: Date.now(), attemptCount: (row.attemptCount || 0) + 1 });

    // PUT to signedUrl
    try {
      const resp = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: blob,
      });
      if (!resp.ok) {
        throw new Error(`upload failed ${resp.status}`);
      }
      // Success: delete file and row
      if (row.opfsPath) {
        await deleteOPFSFile(row.opfsPath).catch(() => {});
      }
      await deleteChunk(row.id);
      await this.maybeComplete(item.trackId);
    } catch (e: any) {
      const delayMs = this.backoffMs(row.attemptCount || 0);
      await updateChunkStatus(row.id, 'failed', { lastError: e?.message ?? 'upload error', lastAttemptAt: Date.now() });
      // Retry later
      setTimeout(() => this.enqueue(item), delayMs);
    }
  }

  private async getSignedUrlForChunk(localRecordingId: string, seq: number): Promise<string | null> {
    const rec = (await LocalRecordingDBInstance.recordings.get(localRecordingId)) as RecordingRow | undefined;
    if (!rec) return null;

    // For existing recordings, localRecordingId may already be the server trackId.
    const explicitServerTrackId = rec.serverTrackId;
    const serverTrackId =
      explicitServerTrackId && typeof explicitServerTrackId === 'string' && explicitServerTrackId.trim() !== ''
        ? explicitServerTrackId.trim()
        : (rec.trackId && typeof rec.trackId === 'string' && !rec.trackId.startsWith('local-') && rec.trackId.trim() !== ''
            ? rec.trackId.trim()
            : null);
    if (!serverTrackId) return null;

    const uploadToken = rec.uploadToken;
    if (!uploadToken || typeof uploadToken !== 'string' || uploadToken.trim() === '') return null;

    const batchSize = rec.batchSize && Number.isFinite(rec.batchSize) ? Math.max(1, Math.floor(rec.batchSize)) : 50;
    const neededBatch = Math.floor(seq / batchSize);
    const indexInBatch = seq % batchSize;

    const cacheKey = `${serverTrackId}:${neededBatch}`;
    let urls = this.signedUrlBatchCache.get(cacheKey);
    if (!urls) {
      const url = `${BASE_URL}/recording/segments/urls/${encodeURIComponent(serverTrackId)}/${neededBatch}`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${uploadToken}` },
      });
      if (!resp.ok) {
        const err: any = new Error(`signed urls fetch failed ${resp.status}`);
        err.status = resp.status;
        throw err;
      }
      const data = await resp.json().catch(() => null);
      if (!data || !Array.isArray(data.urls)) throw new Error('invalid urls response');
      urls = data.urls;
      this.signedUrlBatchCache.set(cacheKey, urls);
    }

    const signedUrl = urls[indexInBatch];
    if (!signedUrl || typeof signedUrl !== 'string') throw new Error('missing signed url in batch');
    return signedUrl;
  }

  private backoffMs(attempts: number): number {
    // Exponential backoff with jitter: 1s, 2s, 4s, 8s, up to 60s
    const base = Math.min(60000, 1000 * Math.pow(2, Math.max(0, attempts)));
    const jitter = Math.floor(Math.random() * 500);
    return base + jitter;
  }

  private async maybeComplete(trackId: string): Promise<void> {
    if (!this.onComplete) return;
    const rec = await LocalRecordingDBInstance.recordings.get(trackId);
    if (!rec) return;
    if (!rec.isRecordingDone || typeof rec.totalChunksCount !== 'number') return;
    const remaining = await LocalRecordingDBInstance.chunks.where('trackId').equals(trackId).count();
    if (remaining > 0) return;
    
    // All chunks uploaded: call onComplete with retries
    // This is critical - the backend sets uploadCompleted: true only after this succeeds
    const maxRetries = 5;
    let attempt = 0;
    
    while (attempt < maxRetries) {
      try {
        console.log(`[Uploader] Calling /recording/complete for ${trackId}, attempt ${attempt + 1}/${maxRetries}`);
        await this.onComplete(trackId, rec.totalChunksCount);
        console.log(`[Uploader] ✅ /recording/complete succeeded for ${trackId}`);
        // Cleanup recording row only after backend confirms
        await LocalRecordingDBInstance.recordings.delete(trackId).catch(() => {});
        return; // Success - exit
      } catch (e: any) {
        attempt++;
        console.error(`[Uploader] ❌ /recording/complete failed for ${trackId}, attempt ${attempt}/${maxRetries}:`, e?.message);
        
        if (attempt < maxRetries) {
          // Exponential backoff: 2s, 4s, 8s, 16s, 32s
          const delayMs = Math.min(32000, 2000 * Math.pow(2, attempt - 1));
          console.log(`[Uploader] Retrying in ${delayMs}ms...`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }
    
    // All retries exhausted - log error but keep recording row for future retry
    console.error(`[Uploader] ⚠️ /recording/complete failed after ${maxRetries} attempts for ${trackId}. Recording row kept for manual retry.`);
  }
}

const uploader = new BackgroundUploader();

async function finalizeStaleRecordingsOnInit(): Promise<void> {
  const now = Date.now();
  const recordings = await LocalRecordingDBInstance.recordings.toArray();

  for (const rec of recordings) {
    try {
      const chunks = await LocalRecordingDBInstance.chunks.where('trackId').equals(rec.trackId).toArray();
      if (chunks.length === 0) {
        // If nothing was ever written and it's been a while, tidy up the dangling recording row.
        if (!rec.isRecordingDone && now - rec.createdAt > STALE_RECORDING_IDLE_MS) {
          await LocalRecordingDBInstance.recordings.delete(rec.trackId).catch(() => {});
        }
        continue;
      }

      const maxSeq = Math.max(...chunks.map((c) => c.seq));
      const latestChunkAt = Math.max(...chunks.map((c) => c.createdAt || 0));

      // If we already marked done but forgot totalChunksCount (crash mid-stop), fix it.
      if (rec.isRecordingDone) {
        if (typeof rec.totalChunksCount !== 'number') {
          await LocalRecordingDBInstance.recordings.update(rec.trackId, {
            totalChunksCount: maxSeq + 1,
          });
        }
        continue;
      }

      // Recording wasn't marked done. If it's been idle, finalize so uploads can complete.
      if (now - latestChunkAt > STALE_RECORDING_IDLE_MS) {
        console.warn(`[Uploader] Finalizing stale recording ${rec.trackId} (maxSeq=${maxSeq})`);
        await LocalRecordingDBInstance.recordings.update(rec.trackId, {
          isRecordingDone: true,
          totalChunksCount: maxSeq + 1,
        });
      }
    } catch (e) {
      console.warn('[Uploader] finalizeStaleRecordingsOnInit failed for', rec.trackId, e);
    }
  }
}

// Public API
export function startBackgroundUploader(opts: { onComplete: OnCompleteFn }) {
  uploader.configure(opts);
  uploader.start();
}

export async function resumePendingOnInit(): Promise<void> {
  // If the app crashed mid-capture, we may have chunks on disk but never marked the recording as done.
  // Finalize those recordings so /recording/complete can run once all chunks upload.
  await finalizeStaleRecordingsOnInit();

  // Reset any 'uploading' chunks to 'pending' (in case of prior crash)
  const uploading = await LocalRecordingDBInstance.chunks.where('status').equals('uploading').toArray();
  for (const c of uploading) {
    await updateChunkStatus(c.id, 'pending');
  }
  const pend = await LocalRecordingDBInstance.chunks.where('status').anyOf('pending', 'failed').toArray();
  for (const c of pend) {
    uploader.enqueue({ id: c.id, trackId: c.trackId });
  }
}

export function enqueueChunkUpload(id: string, trackId: string) {
  uploader.enqueue({ id, trackId });
}
